/**
 * js/client-status.js — the ONE definition of "is this client still live work?"
 *
 * CLIENT_HISTORY is an append-and-evict recency list capped at 200. Every
 * client the agent has ever quoted sat in one flat list until it fell off the
 * end, so the list they use to RESUME work was mostly clients they finished
 * weeks ago. A client can now be filed away: marked Sold (the job is done and
 * it earned the agency money) or Archived (filed away quietly).
 *
 * ONE `status` field, not two booleans. Sold IS filed — the badge is the only
 * difference — and two independent flags would allow the meaningless
 * sold-but-not-filed and archived-and-also-sold states.
 *
 * Loads EAGERLY from index.html, right after js/utils.js: the dashboard's
 * Recent Clients card and the Cmd+K palette both read CLIENT_HISTORY on a cold
 * dashboard, before any plugin's lazy `scripts:` run. Same rule as
 * cgl-utils.js / hawksoft-link.js / folder-ics.js.
 *
 * It also owns `carry()`, which is what keeps record-level fields alive across
 * the fresh-entry rebuild both save paths perform: `CARRIED` is the frozen list
 * of them (the undo ring and the quote's pipeline mark, alongside status).
 *
 * Deliberately dependency-free (no Utils, no STORAGE_KEYS) so it can load
 * first, and it returns data, never HTML, so escaping stays at the call site.
 * Consumers must read `window.ClientStatus` at CALL time and degrade to
 * "show everything" when it is absent — a module that failed to load must not
 * empty a list.
 */
window.ClientStatus = (() => {
    'use strict';

    const ACTIVE = 'active';
    const SOLD = 'sold';
    const ARCHIVED = 'archived';

    // The two filed states, in the order the filter chips show them.
    const FILED = Object.freeze([SOLD, ARCHIVED]);
    const VIEWS = Object.freeze([ACTIVE, SOLD, ARCHIVED, 'all']);

    const BADGES = Object.freeze({
        [SOLD]:     { label: 'Sold',     className: 'chist-badge-sold' },
        [ARCHIVED]: { label: 'Archived', className: 'chist-badge-archived' },
    });

    /**
     * Fails OPEN. Every entry saved before this shipped has no `status`, and a
     * future junk value (a hand-edited blob, a half-merged sync row) must not
     * make a client disappear from the only list that shows them. Anything
     * this function does not recognise is active.
     */
    function statusOf(entry) {
        const raw = entry && typeof entry === 'object' ? entry.status : null;
        return (raw === SOLD || raw === ARCHIVED) ? raw : ACTIVE;
    }

    function isFiled(entry) {
        return statusOf(entry) !== ACTIVE;
    }

    /**
     * No "but always keep the open client" escape hatch. It was tried and it is
     * wrong twice over: it renders a row whose Sold badge contradicts the Active
     * chip sitting above it, and it puts a caller's special case inside a pure
     * predicate. Filing a client from the Active view makes its row leave, and
     * that IS the confirmation — the toast names it, the Restore toast repeats
     * its status, and #activeClientBadge says who is open from every step.
     */
    function filter(clients, view) {
        if (!Array.isArray(clients)) return [];
        const wanted = VIEWS.indexOf(view) >= 0 ? view : ACTIVE;
        if (wanted === 'all') return clients.slice();
        return clients.filter((c) => statusOf(c) === wanted);
    }

    function visible(clients) {
        return filter(clients, ACTIVE);
    }

    function counts(clients) {
        const out = { active: 0, sold: 0, archived: 0, all: 0 };
        if (!Array.isArray(clients)) return out;
        for (const c of clients) {
            out[statusOf(c)] += 1;
            out.all += 1;
        }
        return out;
    }

    /**
     * Returns a NEW entry rather than mutating, so a caller holding the old one
     * cannot half-apply the change. Going back to active DELETES both fields
     * instead of writing `status: 'active'`, so a reopened client is byte-identical
     * to one that was never filed and the blob does not grow a field per entry.
     */
    function applyStatus(entry, status) {
        const next = Object.assign({}, entry);
        if (status === SOLD || status === ARCHIVED) {
            next.status = status;
            next.statusAt = new Date().toISOString();
        } else {
            delete next.status;
            delete next.statusAt;
        }
        return next;
    }

    /**
     * Fields that belong to the RECORD rather than to the form, and so must
     * survive the fresh-entry rebuild whatever the filing status is. Frozen and
     * named in one place so a new record-level field is a one-line addition
     * instead of a fourth place to forget.
     *
     * `status`/`statusAt` are deliberately NOT in here: they are handled below,
     * because an active entry has to stay byte-identical to one that was never
     * filed (applyStatus DELETES both rather than writing status:'active').
     */
    const CARRIED = Object.freeze(['history', 'quoteStatus', 'quoteStatusAt']);

    /**
     * THE reason this module exists as more than a predicate.
     *
     * App.saveClient() and App.autoSaveClient() both build a fresh entry
     * literal, splice the old entry out and unshift the new one. Any field not
     * named in that literal is silently dropped — so without this, archiving a
     * client and then typing one character into their form would un-archive
     * them, invisibly, on the next autosave. The same rebuild drops the undo
     * ring and the quote's pipeline mark, which is why CARRIED exists.
     *
     * There used to be an `if (status === ACTIVE) return newEntry;` fast path
     * here. It is gone: for the common case it carried NOTHING, which is
     * exactly the case a record-level field has to survive.
     *
     * Copy-on-first-write, and `!(k in out)` so a caller-set value wins. Both
     * matter: this must never mutate either argument, and it must not add an
     * undefined-valued key to an entry the previous one did not have.
     */
    function carry(prevEntry, newEntry) {
        if (!newEntry || typeof newEntry !== 'object') return newEntry;
        const prev = prevEntry && typeof prevEntry === 'object' ? prevEntry : null;
        let out = newEntry;
        if (prev) {
            for (const k of CARRIED) {
                if (k in prev && !(k in out)) {
                    if (out === newEntry) out = Object.assign({}, newEntry);
                    out[k] = prev[k];
                }
            }
        }
        const status = statusOf(prev);
        if (status === ACTIVE) return out;
        out = Object.assign({}, out, { status });
        const at = prev && prev.statusAt;
        if (typeof at === 'string' && at) out.statusAt = at;
        return out;
    }

    /** `{ label, className }` for a filed row, or null for an active one. */
    function badge(entry) {
        return BADGES[statusOf(entry)] || null;
    }

    return {
        ACTIVE, SOLD, ARCHIVED, FILED, VIEWS,
        CARRIED,
        statusOf,
        isFiled,
        filter,
        visible,
        counts,
        applyStatus,
        carry,
        badge,
    };
})();
